import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaVideo,
  FaCheck,
  FaClock,
  FaTimes,
  FaArrowLeft,
  FaBolt,
  FaUsers,
  FaMicrophone,
  FaPlus
} from "react-icons/fa";
import { motion } from "framer-motion";

const PaidConferenceDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({
    new: 0,
    accepted: 0,
    rejected: 0,
    completed: 0
  });

  useEffect(() => {
    const fetchAllCounts = async () => {
      setLoading(true);
      try {
        const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };

        const [resNew, resAccepted, resRejected, resCompleted] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/reporter/get/new/paid-conferences`, { headers }),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/reporter/get/accepted/paid-conferences`, { headers }),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/reporter/get/rejected/paid-conferences`, { headers }),
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/reporter/get/completed/paid-conferences`, { headers })
        ]);

        const getCount = (res) => Array.isArray(res.data) ? res.data.length : (res.data?.data?.length || 0);

        setCounts({
          new: getCount(resNew),
          accepted: getCount(resAccepted),
          rejected: getCount(resRejected),
          completed: getCount(resCompleted)
        });
      } catch (error) {
        console.error("Error fetching paid conference counts:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllCounts();
  }, []);

  const cards = [
    { title: "New Invites", icon: <FaPlus />, count: counts.new, path: "/reporter/paidconference/new", desc: "Premium events", text: "text-blue-600" },
    { title: "Accepted", icon: <FaCheck />, count: counts.accepted, path: "/reporter/paidconference/accepted", desc: "Live sessions", text: "text-emerald-600" },
    { title: "Rejected", icon: <FaTimes />, count: counts.rejected, path: "/reporter/paidconference/rejected", desc: "Dismissed logs", text: "text-rose-600" },
    { title: "Completed", icon: <FaClock />, count: counts.completed, path: "/reporter/paidconference/completed", desc: "History & Paid", text: "text-purple-600" },
  ];

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Premium Compact Header */}
      <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 px-4 pt-6 pb-20 text-white">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-white/10 flex items-center justify-center text-indigo-300 shadow-inner">
              <FaVideo className="text-xl" />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,0.6)]"></span>
                <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">Premium Media</span>
              </div>
              <h1 className="text-2xl font-black text-white leading-tight">
                Paid <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-blue-300 drop-shadow-[0_2px_10px_rgba(79,70,229,0.4)]">Conferences.</span>
              </h1>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3">
            {/* Conference Type Switch */}
            <div className="flex items-center bg-white/5 p-1 rounded-xl border border-white/10 backdrop-blur-md">
              <button
                onClick={() => navigate('/reporter/freeconference')}
                className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${location.pathname.includes('freeconference') ? 'bg-white text-slate-900 shadow-lg' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
              >
                Free
              </button>
              <button
                onClick={() => navigate('/reporter/paidconference')}
                className={`px-4 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${location.pathname.includes('paidconference') ? 'bg-white text-slate-900 shadow-lg' : 'text-white/60 hover:text-white hover:bg-white/5'}`}
              >
                Paid
              </button>
            </div>

            <button
              onClick={() => navigate('/reporter/dashboard')}
              className="group flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-all active:scale-95 border border-white/10"
            >
              <FaArrowLeft className="group-hover:-translate-x-1 transition-transform" />
              Back Hub
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-5xl mx-auto px-4 -mt-10 pb-8 space-y-6">

        {/* Compact Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {loading ? (
            Array(4).fill(0).map((_, i) => (
              <div key={i} className="h-28 bg-white rounded-2xl shadow-xl border border-slate-100 animate-pulse" />
            ))
          ) : (
            cards.map((card, index) => (
              <motion.button
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => navigate(card.path)}
                className={`group bg-white p-4 rounded-2xl shadow-xl shadow-slate-200/50 border border-slate-100 transition-all text-left flex flex-col justify-between h-32 active:scale-95 relative overflow-hidden ${card.text.replace('text-', 'hover:border-').replace('600', '300')} ${card.text.replace('text-', 'hover:shadow-').replace('600', '100')}/50`}
              >
                <div className={`absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity ${card.text.replace('text-', 'bg-')}`} />
                <div className="flex justify-between items-start">
                  <div className={`p-2 rounded-xl bg-slate-50 text-slate-400 group-hover:bg-white/50 transition-colors ${card.text.replace('text-', 'group-hover:text-')}`}>
                    {React.cloneElement(card.icon, { className: "w-4 h-4" })}
                  </div>
                  <span className={`text-2xl font-black tracking-tighter transition-colors ${card.text}`}>
                    {card.count}
                  </span>
                </div>

                <div>
                  <h2 className={`font-black text-xs uppercase tracking-tight ${card.text}`}>{card.title}</h2>
                  <p className="text-slate-400 text-[9px] font-black mt-0.5 truncate uppercase tracking-tighter">{card.desc}</p>
                </div>
              </motion.button>
            ))
          )}
        </div>

        {/* Action Panel */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 bg-white rounded-2xl p-6 border border-slate-100 shadow-xl shadow-slate-200/50 flex flex-col sm:flex-row items-center gap-6 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-24 h-24 bg-indigo-50 rounded-full -mr-12 -mt-12 blur-2xl opacity-60" />

            <div className="w-14 h-14 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center flex-shrink-0 relative z-10">
              <FaMicrophone className="text-2xl" />
            </div>

            <div className="flex-1 text-center sm:text-left relative z-10">
              <h3 className="text-slate-900 font-black text-base uppercase tracking-tight">Revenue Analysis</h3>
              <p className="text-slate-500 text-[11px] font-bold mt-1 leading-relaxed max-w-sm uppercase tracking-wide">
                Track your premium conference earnings and participation value across all exclusive media events.
              </p>
            </div>

            <button className="px-5 py-2.5 bg-slate-900 hover:bg-indigo-600 text-white text-[10px] font-black uppercase tracking-widest rounded-xl transition-colors relative z-10">
              Value Report
            </button>
          </div>

          <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6 flex flex-col justify-between">
            <div className="flex items-start justify-between mb-4">
              <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center text-emerald-600 shadow-sm border border-emerald-100/50">
                <FaBolt className="text-lg" />
              </div>
              <span className="text-[9px] font-black text-emerald-600 bg-white px-2 py-0.5 rounded-full uppercase tracking-widest border border-emerald-100">Premium</span>
            </div>
            <div>
              <h4 className="text-emerald-900 font-black text-xs uppercase tracking-tight">Pay Cycles</h4>
              <p className="text-emerald-700/70 text-[10px] font-bold mt-1 leading-tight uppercase">High-value paid conferences are currently processing invites. Secure your slot.</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default PaidConferenceDashboard;
